import {useState} from 'react';
import {faArrowDown, faCheck, faTrash} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import styles from '../../style.module.scss';
import {
  costPerClickFormatter,
  searchVolumeFormatter,
  isScaPublicPage,
} from '@/components/dashboard/pages/landing-page-optimizer/page-editor/LeftSide/EditorSection/content-editor/utils';
import {
  Divider,
  Popover,
  Tooltip,
  Spin,
} from 'antd';
import {NextImg} from '@/utils/nextImg';

import {getTwoGreenPercentageColor} from '@/utils/colors';
import classNames from 'classnames';
import {getLocalesByKey as t} from '@/utils/translation';
import {CurrentPageStore, FocusTermModel} from '@/store/content-optimizer/currentPage';
import {observer} from 'mobx-react';
import {getDomain, getSiteName} from '@/utils/url';
import {isNil} from 'lodash';
import {LoadingOutlined} from '@ant-design/icons';
import styled from 'styled-components';
import {faWikipediaW} from '@fortawesome/free-brands-svg-icons';

interface Props {
  focusTerm: FocusTermModel;
  i: number;
  store: CurrentPageStore;
}

export const SingleTerm = observer(({focusTerm, i, store}: Props) => {
  const [copySuccess, setCopySuccess] = useState(false);
  const [isRemoving, setIsRemoving] = useState<boolean>(false);

  const antIcon = <LoadingOutlined style={{fontSize: 14, color: '#7f4ead', marginLeft: 3}} spin />;

  const removeTermHandler = async (term: string) => {
    try {
      setIsRemoving(true);
      await store.removeTerms(term);
      setIsRemoving(false);
    } catch {
      setIsRemoving(false);
    }
  };

  const termContainerSetup = {
    'include-more': {
      background: 'rgba(241, 170, 62, 0.15)',
      hoverBackground: 'rgba(241, 170, 62, 0.25)',
      icon: null,
    },
    'included': {
      background: 'rgba(31, 172, 71, 0.15)',
      hoverBackground: 'rgba(31, 172, 71, 0.25)',
      icon: null,
    },
    'include-less': {
      background: 'rgba(244, 67, 67, 0.15)',
      hoverBackground: 'rgba(244, 67, 67, 0.25)',
      icon: <FontAwesomeIcon icon={faArrowDown} color='#F44343' style={{marginRight: 5}}/>,
    },
  };

  const fitTermInQuota = (term: string, quote?: string ) => {
    if (!quote) return '';

    // this check should not be nessesary as we expect BE to include term in quote always
    if (quote.includes(`<b>${term}</b>`)) {
      const startIndex = quote.indexOf(`<b>${term}</b>`);

      if (startIndex > (140 - term.length)) {
        return quote.slice(startIndex - (140 - term.length));
      } else {
        return quote;
      }
    }
  };

  const isAnyFocusTermLink = () => {
    if (focusTerm?.wikipediaLink || focusTerm?.wikidataLink || focusTerm?.knowledgeGraphLink || focusTerm?.crunchbaseLink) {
      return true;
    } else {
      return false;
    }
  };

  return (<Popover
    key={`${i}`}
    overlayClassName={styles.focusTermPopover}
    trigger='hover'
    placement='leftTop'
    autoAdjustOverflow={true}
    content={
      <div>
        <p className={styles.popOverTermsTitle}>Topical term</p>
        <p style={{padding: '0px 21px'}}>{focusTerm.name}</p>
        <div style={{padding: '0px 21px'}}>
          {focusTerm.sv !== undefined && focusTerm.sv !== null && <span style={{marginRight: '15px', fontSize: '12px', color: '#4E5156'}}>{`SV: ${searchVolumeFormatter(focusTerm.sv)}`}</span>}
          {focusTerm.cpc !== undefined && focusTerm.cpc !== null && <span style={{fontSize: '12px', color: '#4E5156'}}>{`CPC: ${costPerClickFormatter(focusTerm.cpc)}`}</span>}
        </div>
        {focusTerm?.foundIn && focusTerm?.foundIn.length > 0 ?
          <div className={styles.focusTermsUsedIn}>
            <span style={{color: '#ddd'}}>Used in &nbsp;</span>
            {focusTerm.foundIn.map((item, i)=> {
              if (focusTerm.foundIn.length === 1) {
                return <span style={{color: '#fff', fontWeight: 'bold'}} key={`${item}`}>{`<${item}>`}</span>;
              } else {
                if (i === focusTerm.foundIn.length - 1) {
                  return <><span style={{color: '#ddd', fontWeight: 'normal'}} key={`${item}`}>and &nbsp;</span><span style={{color: '#fff', fontWeight: 'bold'}}>{`<${item}>`}</span></>;
                } else if (i === focusTerm.foundIn.length - 2) {
                  return <span style={{color: '#fff', fontWeight: 'bold'}} key={`${item}`}>{`<${item}>`} &nbsp;</span>;
                } else {
                  return <span style={{color: '#fff', fontWeight: 'bold'}} key={`${item}`}>{`<${item}>`}, &nbsp;</span>;
                }
              }
            })}
          </div> :
          null}
        <h5 className={styles.popOverTitle}>{'Current / Recommended # of uses'}</h5>
        <p style={{padding: '0px 21px'}}>
          {store?.content?.targetKewyordsOnlyArr?.includes(focusTerm.name) ? <div style={{color: '#A3A4A4', marginBottom: 10, fontSize: 12}}>Target keywords do not have any usage limitations.</div> : null}
          <span style={{fontWeight: 700, color: getTwoGreenPercentageColor(focusTerm.percentage)}}>{focusTerm.usedCount}</span>
          <span style={{color: '#121212'}}>&nbsp;of&nbsp;{focusTerm.requiredCountMin}</span>
          <span style={{color: '#121212', fontSize: 12, marginLeft: 5}}>{`(Max: ${focusTerm.requiredCountMax || 0})`}</span>
        </p>
        <h5 className={styles.popOverTitle}>{'Competitors using this term'}</h5>
        <div className={styles.competitors}>
          {focusTerm?.competitors?.results?.map((competitor, index) => (
            <Popover
              overlayClassName={classNames(styles.focusTermPopover, styles.withArrow, styles.innerPopover)}
              align={{
                offset: [5, 0],
              }}
              key={index}
              placement='left'
              content={<div style={{padding: '10px 21px'}}>
                <p>{competitor.title ?? getSiteName(competitor.hostname)}</p>
                <a className={classNames(styles.singleCompetitorUrl, styles.innerSingleCompetitorUrl)} href={competitor.url} target='_blank' rel='noreferrer'>{competitor.url}</a>
                <div className={styles.snippetsContainer}>
                  {competitor?.snippets?.map((snippet, idx) => {
                    return <div key={idx} className={styles.singleSnippetContainer}>
                      <p className={styles.singleSnippet}
                        dangerouslySetInnerHTML={{__html: fitTermInQuota(focusTerm.name, snippet)}}
                      >
                        {/* {snippet.length > 130 ? snippet.substring(0, 130) + '...' : snippet} */}
                      </p>
                      {idx !== (competitor?.snippets?.length - 1) && <Divider style={{background: '#3e4043', marginTop: 10, marginBottom: 10}}/>}
                    </div>;
                  })}
                </div>
              </div>}>
              <div className={styles.singleCompetitor}>
                <span style={{color: '#a3a4a4'}}>{`${index + 1}. `}</span><NextImg style={{width: 20, height: 20}} src={`https://www.google.com/s2/favicons?sz=16&domain_url=${competitor.url}`} />
                <a className={styles.singleCompetitorUrl} href={competitor.url} target='_blank' rel='noreferrer'>{getDomain(competitor.url)}</a>
              </div>
            </Popover>
            // <a key={index} className={styles.competitor} href={competitor.url} target='_blank' rel='noreferrer'>{`${index + 1}. `}{competitor.hostname}</a>
          ))}

        </div>
        {(!isNil(focusTerm?.normalizedScore) || focusTerm?.source) ? <Divider className={styles.popoverDivider} /> : null}
        <LowerSection>
          {focusTerm?.source ?
            <p style={{color: '#4E5156', fontSize: '12px', marginTop: '0px'}}>{`Keyword source: `}<span style={{color: '#121212'}}>{focusTerm?.source?.join(', ')}</span></p>: null}
          {!isNil(focusTerm?.normalizedScore) ? <>
            <p style={{color: '#4E5156', fontSize: '12px'}}>{`Importance score: `}<span style={{color: '#121212'}}>{focusTerm.normalizedScore}</span></p>
          </>: null
          }
          {isAnyFocusTermLink() ?
            <ReferencesWrapper>
              <ReferencesTitle style={{color: '#4E5156', fontSize: '12px'}}>Entity References</ReferencesTitle>
              <LinksWrapper>
                {focusTerm?.crunchbaseLink ?
                  <SingleLink onClick={() => window.open(focusTerm?.crunchbaseLink)} style={{display: 'flex'}}>
                    <NextImg style={{width: 16, height: 16, borderRadius: 4}} src={'/img/icon/crunchBaseLogo.png'}/>
                    <span>Crunchbase</span>
                  </SingleLink> : ''}
                {focusTerm?.knowledgeGraphLink ?
                  <SingleLink onClick={() => window.open(focusTerm?.knowledgeGraphLink)} style={{display: 'flex'}}>
                    <NextImg style={{width: 16, height: 16}} src={'/img/icon/google.svg'}/>
                    <span>Google</span>
                  </SingleLink> : ''}
                {focusTerm?.wikipediaLink ?
                  <SingleLink onClick={() => window.open(focusTerm?.wikipediaLink)} style={{display: 'flex'}}>
                    <FontAwesomeIcon fontSize={14} icon={faWikipediaW} />
                    <span>Wikipedia</span>
                  </SingleLink> : ''}
                {focusTerm?.wikidataLink ?
                  <SingleLink onClick={() => window.open(focusTerm?.wikidataLink)} style={{display: 'flex'}}>
                    <NextImg style={{width: 16, height: 16}} src={'/img/icon/wikiDataIcon.svg'}/>
                    <span>Wikidata</span>
                  </SingleLink> : ''}
              </LinksWrapper>
            </ReferencesWrapper> : ''}
        </LowerSection>
      </div>
    }
  >

    <TermContainer>
      <Left background={termContainerSetup[focusTerm.status].background} hoverBackground={termContainerSetup[focusTerm.status].hoverBackground}>
        <TermName>{focusTerm.name}</TermName>
        {focusTerm?.isBranded && <BrandedLabel>{'branded'}</BrandedLabel>}
        {focusTerm?.foundIn && focusTerm.foundIn.map((item, idx) => {
          return <FoundInLabel key={idx}>{item}</FoundInLabel>;
        })}
      </Left>
      <Right border={termContainerSetup[focusTerm.status].background}>
        {termContainerSetup[focusTerm.status].icon}
        <span>{focusTerm.usedCount}</span>&nbsp;/&nbsp;<span style={{color: '#A3A4A4'}}>{focusTerm.requiredCountMin}</span>

        <div className='termControllsContainer'>
          <Tooltip
            title={copySuccess ? <span><FontAwesomeIcon icon={faCheck} style={{color: '#2AC155', fontSize: 12}}/>{` Copied to clipboard`}</span> : t('copy-to-clipboard')}
            trigger='hover'
            placement='bottom'
            overlayClassName={styles.copyTooltip}
            onVisibleChange={visible => {
              if (!visible) {
                setTimeout(() => {
                  setCopySuccess(false);
                }, 300);
              }
            }}>
            <CopyIcon onClick={() => {
              navigator.clipboard.writeText(focusTerm.name);
              setCopySuccess(true);
            }}>
              <NextImg width={13} height={13} style={{width: '11px', marginLeft: '5px'}} src={'/img/copy-white.svg'}/>
            </CopyIcon>
          </Tooltip>

          {!isScaPublicPage() ? <Tooltip
            title={'Remove Term'}
            trigger='hover'
            placement='bottom'
            overlayClassName={styles.copyTooltip}
            onVisibleChange={visible => {
              if (!visible) {
                setTimeout(() => {
                  setCopySuccess(false);
                }, 300);
              }
            }}>
            {(isRemoving && store.isRemovingFocusTerms) ? <Spin indicator={antIcon} /> :
              <div onClick={() => removeTermHandler(focusTerm.name)}>
                <FontAwesomeIcon icon={faTrash} style={{marginLeft: 5}}/>
              </div>}

          </Tooltip> : null}
        </div>
      </Right>
    </TermContainer>
  </Popover>);
});

const TermContainer = styled.div`
  margin-right: 5px;
  margin-left: 5px;
  margin-bottom: 9px;
  display: flex;
  max-width: 300px;
  cursor: default;


  .termControllsContainer {
    position: absolute;
    right: -100%;
    width: 100%;
    height: 100%;
    transition: all .3s ease;
    background: #2D2F34;
    opacity: 1;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding-right: 10px;
  }

  &:hover {
    .termControllsContainer {
      right: 0;
    }
  }
`;
const Left = styled.div<{background: string; hoverBackground: string}>`
  padding: 3px 8px;
  background: ${p => p.background};
  width: 100%;
  border-radius: 36px 0 0 36px;
  display: flex;
  flex-wrap: wrap;
  max-width: 260px;
  align-items: center;
  transition: background .2s ease;

  &:hover {
    background: ${p => p.hoverBackground};
  }
`;
const TermName = styled.div`
  font-size: 13px;
  color: #fff;
  line-height: 13px;
`;
const BrandedLabel = styled.span`
  background-color:  rgba(255, 255, 255, 0.2);
  border-radius: 50px;
  color: #4ebbab;
  font-size: 12px;
  line-height: 15px;
  margin-left: 5px;
  padding: 0 5px;
`;
const FoundInLabel = styled.span`
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 50px;
  display: inline-block;
  font-size: 12px;
  line-height: 15px;
  margin-left: 5px;
  padding: 0 5px;
`;

const Right = styled.div<{border: string}>`
  padding: 5px 7px;
  border: ${p => `2px solid ${p.border}`};
  border-radius: 0 36px 36px 0;
  font-size: 13px;
  display: flex;
  align-items: center;
  line-height: 13px;
  position: relative;
  overflow: hidden;
  min-width: fit-content;
  cursor: pointer;
  


`;

const CopyIcon = styled.div`
`;
const ReferencesTitle = styled.p``;
const ReferencesWrapper = styled.div``;
const LinksWrapper = styled.div`
display: flex;
flex-wrap: wrap;
gap: 8px;
`;
const SingleLink = styled.div`
display: flex;
padding: 3px 6px;
justify-content: center;
align-items: center;
gap: 4px;
border-radius: 6px;
border: 1px solid #E8E8E8;
background:#FFF;
cursor: pointer;
span {
  color: #121212;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
}
`;
const LowerSection = styled.div`
padding: 10px 21px;
background: #f9f9fb;
`;
